/**
 * Device-Based Authentication (Phase 1)
 *
 * Phase 1 (Current):
 * - Device ID identifies the user (no login required)
 * - Dev mode: fixed device ID 'dev' for single-user experience
 * - Production: persistent UUID per browser via localStorage
 *
 * Phase 2 (Future):
 * - Add login (email/OAuth)
 * - On login, migrate device-based cards to the account
 * - Multi-device: link devices to account, dedup later
 */

// Constants for localStorage keys
const STORAGE_KEYS = {
  DEVICE_ID: 'piccard-device-id'
} as const

// Server-side fallback (for SSR compatibility)
const SERVER_DEVICE_ID = 'dev-device-server'

// Development logging flag
const IS_DEVELOPMENT = import.meta.env.DEV

/**
 * Log authentication events (development only)
 */
function logAuth(message: string, ...args: unknown[]): void {
  if (IS_DEVELOPMENT) {
    console.log(`[Auth] ${message}`, ...args)
  }
}

/**
 * Get device ID
 *
 * - Dev mode: always returns fixed 'dev' so all sessions share data
 * - Production: generates and persists a UUID in localStorage
 *
 * @returns Device ID
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') {
    return SERVER_DEVICE_ID
  }

  // Dev mode: fixed device ID for single-user experience
  if (IS_DEVELOPMENT) {
    return 'dev'
  }

  // Production: persistent per-browser device ID
  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID)

  if (!deviceId) {
    deviceId = `device-${crypto.randomUUID()}`
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId)
    logAuth('Created new device ID:', deviceId)
  }

  return deviceId
}

/**
 * Get authentication headers for API requests
 *
 * Sends X-Device-ID header. Server resolves to DB user via middleware.
 *
 * @returns Object containing auth headers
 */
export function getAuthHeaders(): Record<string, string> {
  return {
    'X-Device-ID': getDeviceId()
  }
}

/**
 * Clear all authentication data (logout / reset)
 */
export function clearAuthData(): void {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.removeItem(STORAGE_KEYS.DEVICE_ID)
  logAuth('Cleared auth data')
}
